import type { AudioFormat } from '@namu/protocol'
import { createOpusDecoder, pcm16ToFloat } from './codec'
import type { OpusDecoder } from './codec'

/**
 * Playing the assistant's voice.
 *
 * Audio arrives in twenty millisecond pieces over a network that delivers them
 * unevenly, and it has to come out as continuous speech. That is the whole
 * problem, and it is solved with a scheduling cursor rather than by playing
 * each piece as it lands.
 *
 * Each buffer is scheduled to start exactly where the previous one ended. The
 * Web Audio clock is sample-accurate, so consecutive buffers scheduled that way
 * join with no gap and no click, which is not true of playing them on arrival.
 *
 * A small lead is kept ahead of the clock to absorb jitter. Too little and
 * every late packet is an audible gap; too much and the assistant answers
 * noticeably after it should. A tenth of a second is roughly where speech
 * stops sounding broken and stays feeling immediate.
 */

/** How far ahead of the audio clock to stay. */
const TARGET_LEAD_S = 0.12

/**
 * When the cursor has fallen this far behind, the stream has stalled and is
 * restarted rather than caught up. Trying to catch up means playing a burst of
 * stale audio at once, which is worse than the gap it is covering.
 */
const RESET_AFTER_S = 0.4

export interface Playback {
  /** Queues one frame for the given turn. */
  push(turn: number, payload: Uint8Array): void
  /**
   * Stops immediately and drops anything queued.
   *
   * Used for barge-in. The point of an interruption is that the assistant stops
   * now, so every buffer already scheduled has to be cancelled, not left to
   * play out.
   */
  stop(): void
  /** True while there is audio scheduled that has not finished playing. */
  readonly playing: boolean
  /** How much audio has been played, in milliseconds. For diagnostics. */
  readonly playedMs: number
  close(): Promise<void>
}

export interface PlaybackOptions {
  format: AudioFormat
  /** Notified when the queue empties, so the caller can settle its state. */
  onDrained?: () => void
}

export async function createPlayback(options: PlaybackOptions): Promise<Playback> {
  const { format, onDrained } = options

  /*
   * Its own AudioContext, at the rate the server sends.
   *
   * Creating the context at the source rate means the browser resamples once,
   * in native code, on the audio thread. Doing it any other way means
   * resampling in JavaScript, which is both slower and worse.
   */
  const context = new AudioContext({ sampleRate: format.sampleRate })

  /** Everything routes through one gain node, so stopping is one disconnect. */
  let gain = context.createGain()
  gain.connect(context.destination)

  let cursor = 0
  let currentTurn = -1
  let playedSamples = 0
  let scheduled = new Set<AudioBufferSourceNode>()

  let decoder: OpusDecoder | null = null
  if (format.codec === 'opus') {
    decoder = await createOpusDecoder(format.sampleRate, (samples) => enqueue(samples))
  }

  function enqueue(samples: Float32Array): void {
    if (samples.length === 0) return

    const buffer = context.createBuffer(1, samples.length, format.sampleRate)
    buffer.getChannelData(0).set(samples)

    const source = context.createBufferSource()
    source.buffer = buffer
    source.connect(gain)

    const now = context.currentTime
    if (cursor < now - RESET_AFTER_S || cursor < now) {
      // Either the first frame of a turn, or the stream fell behind while the
      // network was quiet. Restart the cursor ahead of the clock rather than
      // scheduling in the past, which silently plays nothing.
      cursor = now + TARGET_LEAD_S
    }

    source.start(cursor)
    cursor += buffer.duration
    playedSamples += samples.length

    scheduled.add(source)
    source.onended = () => {
      scheduled.delete(source)
      if (scheduled.size === 0) onDrained?.()
    }
  }

  function stopEverything(): void {
    for (const source of scheduled) {
      try {
        source.stop()
      } catch {
        // Already finished. Stopping a stopped source throws.
      }
    }
    scheduled = new Set()

    /*
     * The gain node is replaced rather than reused.
     *
     * A source that has been told to stop can still have a few milliseconds in
     * flight inside the graph. Disconnecting and rebuilding guarantees silence
     * immediately, which is what an interruption has to mean: the person is
     * already talking, and a tail of the abandoned sentence underneath them is
     * exactly the failure the feature exists to prevent.
     */
    gain.disconnect()
    gain = context.createGain()
    gain.connect(context.destination)

    cursor = 0
    currentTurn = -1
  }

  return {
    push(turn, payload) {
      if (turn !== currentTurn) {
        // A new turn. The cursor restarts so the answer begins promptly rather
        // than queued behind whatever was left of the last one.
        currentTurn = turn
        cursor = 0
      }
      if (decoder) decoder.decode(payload)
      else enqueue(pcm16ToFloat(payload))
    },

    stop: stopEverything,

    get playing() {
      return scheduled.size > 0
    },

    get playedMs() {
      return Math.round((playedSamples / format.sampleRate) * 1000)
    },

    async close() {
      stopEverything()
      decoder?.close()
      try {
        await context.close()
      } catch {
        // Already closed.
      }
    },
  }
}
